import { useCallback, useEffect, useRef, useState } from "react";
import { HeroArticle } from "@/components/ArticleCard";
import { ChevronLeft, ChevronRight } from "lucide-react";

const INTERVAL = 6500;
const SWIPE_MIN = 45;

/** Auto-rotating carousel of every featured article, same size as the single hero. Swipe su mobile. */
export function FeaturedCarousel({ articles }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [drag, setDrag] = useState(0);
  const start = useRef(null);
  const swiped = useRef(false);
  const total = articles.length;

  const go = useCallback((next) => setIndex((i) => (next + total) % total), [total]);

  useEffect(() => {
    if (paused || total < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % total), INTERVAL);
    return () => clearInterval(t);
  }, [paused, total]);

  useEffect(() => { if (index >= total) setIndex(0); }, [index, total]);

  const onStart = (x) => { start.current = x; swiped.current = false; setPaused(true); };
  const onMove = (x) => {
    if (start.current === null) return;
    const d = Math.max(-140, Math.min(140, x - start.current));
    if (Math.abs(d) > 8) swiped.current = true;
    setDrag(d);
  };
  const onEnd = () => {
    if (start.current !== null && Math.abs(drag) > SWIPE_MIN) go(index + (drag < 0 ? 1 : -1));
    start.current = null;
    setDrag(0);
    setPaused(false);
  };

  const current = articles[Math.min(index, total - 1)];
  if (!current) return null;

  return (
    <div data-testid="featured-carousel"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div
        className="overflow-hidden select-none"
        style={{ touchAction: "pan-y" }}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        onClickCapture={(e) => { if (swiped.current) { e.preventDefault(); e.stopPropagation(); } }}
        onTouchStart={(e) => onStart(e.touches[0].clientX)}
        onTouchMove={(e) => onMove(e.touches[0].clientX)}
        onTouchEnd={onEnd}
        onPointerDown={(e) => { if (e.pointerType !== "touch") onStart(e.clientX); }}
        onPointerMove={(e) => { if (e.pointerType !== "touch") onMove(e.clientX); }}
        onPointerUp={onEnd}
        onPointerLeave={() => { start.current = null; setDrag(0); }}
      >
        <div key={current.id} className={drag ? "" : "fade-up"}
          style={{
            transform: `translateX(${drag}px) rotate(${drag * 0.012}deg)`,
            transition: drag ? "none" : "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
          }}>
          <HeroArticle article={current} />
        </div>
      </div>

      {total > 1 && (
        <div className="flex items-center justify-between gap-3 mt-4">
          <div className="flex items-center gap-2" data-testid="featured-dots">
            {articles.map((a, i) => (
              <button key={a.id} onClick={() => setIndex(i)} aria-label={`Vai all'articolo ${i + 1}`}
                data-testid={`featured-dot-${i}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === index ? "w-7 bg-stone-900" : "w-2 bg-stone-300 hover:bg-stone-500"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-meta text-[10px] uppercase tracking-[0.18em] text-stone-400">
              <span className="sm:hidden">scorri</span>
              <span className="hidden sm:inline">{index + 1}/{total}</span>
            </span>
            <button onClick={() => go(index - 1)} data-testid="featured-prev" aria-label="Precedente"
              className="w-8 h-8 rounded-full border-2 border-line bg-panel flex items-center justify-center text-stone-600 hover:border-gold hover:text-stone-900 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => go(index + 1)} data-testid="featured-next" aria-label="Successivo"
              className="w-8 h-8 rounded-full border-2 border-line bg-panel flex items-center justify-center text-stone-600 hover:border-gold hover:text-stone-900 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
